import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { Button } from "../../components/ui/Button";

/**
 * Tidy up: archive the companies ingest invented that nobody ever used, and
 * restore anything archived by mistake.
 *
 * Two halves of one job, so one panel. The candidates are the server's rule,
 * not a filter here -- auto-created, unlinked, nobody's, no order or quote
 * ever, and no timeline entry or ticket inside the look-back -- because the
 * flag alone would offer accounts people have since worked (see
 * `ArchiveService`). Every company is ticked to begin with and each is checked
 * again when the button is pressed, so a list left open over lunch cannot
 * archive somebody a rep rang in the meantime.
 *
 * Admin-only, as the routes are.
 */

const LOOK_BACKS = [6, 12, 24] as const;

export function ArchiveSettings() {
  return (
    <div className="flex flex-col gap-6">
      <TidyUp />
      <Archived />
    </div>
  );
}

function TidyUp() {
  const queryClient = useQueryClient();
  const [months, setMonths] = useState<number>(12);
  const [unticked, setUnticked] = useState<Set<number>>(new Set());

  const candidates = useQuery({
    queryKey: queryKeys.crmArchiveCandidates(months),
    queryFn: () => crmApi.archiveCandidates(months),
    staleTime: 60_000,
  });

  // A new look-back is a new list; what was unticked on the old one means nothing.
  useEffect(() => setUnticked(new Set()), [months]);

  const rows = candidates.data?.rows ?? [];
  const chosen = useMemo(() => rows.filter((row) => !unticked.has(row.id)), [rows, unticked]);

  const archive = useMutation({
    mutationFn: () => crmApi.bulkArchive(chosen.map((row) => row.id), months),
    onSuccess: () => {
      setUnticked(new Set());
      void queryClient.invalidateQueries({ queryKey: ["crm"] });
    },
  });

  const toggle = (id: number): void =>
    setUnticked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm text-content">
        <span>Companies ingest created that nobody has used for</span>
        <select
          value={months}
          onChange={(event) => setMonths(Number(event.target.value))}
          className="h-7 rounded-md border border-line bg-surface px-2 text-sm"
        >
          {LOOK_BACKS.map((value) => (
            <option key={value} value={value}>
              {value} months
            </option>
          ))}
        </select>
      </div>
      <p className="text-[11px] text-muted">
        Auto-created, not linked to Team, with no account manager, no order or
        quote ever, and no timeline entry or ticket in that time. Archiving
        returns their people to the unfiled queue; each can be restored below.
      </p>

      {candidates.isLoading ? (
        <p className="text-xs text-faint">Looking…</p>
      ) : candidates.isError ? (
        <p className="text-xs text-sla-breached">Could not load the candidates.</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-faint">Nothing to tidy up.</p>
      ) : (
        <>
          <p className="text-xs text-muted">
            {candidates.data!.total > rows.length
              ? `${candidates.data!.total} companies match; the first ${rows.length} are shown. Archive these, then the next lot appears.`
              : `${rows.length} ${rows.length === 1 ? "company matches" : "companies match"}.`}
          </p>
          <div className="max-h-80 overflow-y-auto rounded-md border border-line">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-surface-2 text-left text-faint">
                <tr>
                  <th className="w-8 px-2 py-1">
                    <input
                      type="checkbox"
                      aria-label="Tick every company"
                      checked={unticked.size === 0}
                      onChange={() =>
                        setUnticked(unticked.size === 0 ? new Set(rows.map((row) => row.id)) : new Set())
                      }
                    />
                  </th>
                  <th className="px-2 py-1">Company</th>
                  {/* Not on a phone. The name is a link to the company, and a
                      domain column beside it pushed Last activity -- the reason
                      anything is on this list -- off the side of the box. */}
                  <th className="px-2 py-1 max-sm:hidden">Domain</th>
                  <th className="px-2 py-1 text-right">People</th>
                  <th className="px-2 py-1">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-line">
                    <td className="px-2 py-1">
                      <input
                        type="checkbox"
                        aria-label={`Archive ${row.name}`}
                        checked={!unticked.has(row.id)}
                        onChange={() => toggle(row.id)}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Link to={`/crm/companies/${row.id}`} className="text-brand-600 hover:underline">
                        {row.name}
                      </Link>
                    </td>
                    <td className="px-2 py-1 text-muted max-sm:hidden">{row.domain ?? "—"}</td>
                    <td className="px-2 py-1 text-right text-muted">{row.contacts}</td>
                    <td className="px-2 py-1 text-muted">
                      {formatDate(row.lastActivityAt) ?? `never (added ${formatDate(row.createdAt)})`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              variant="danger"
              disabled={chosen.length === 0 || archive.isPending}
              onClick={() => archive.mutate()}
            >
              {archive.isPending
                ? "Archiving…"
                : `Archive ${chosen.length} ${chosen.length === 1 ? "company" : "companies"}`}
            </Button>
            {archive.isError ? (
              <span className="text-xs text-sla-breached">
                {archive.error instanceof ApiError ? archive.error.message : "Could not archive them."}
              </span>
            ) : null}
          </div>
        </>
      )}

      {/* Outside the list, which is empty once the last of it is archived. */}
      {archive.data ? (
        <p className="text-xs text-muted">
          Archived {archive.data.archived}
          {archive.data.skipped > 0
            ? `; ${archive.data.skipped} skipped because they no longer qualify`
            : ""}
          .
        </p>
      ) : null}
    </section>
  );
}

function Archived() {
  const queryClient = useQueryClient();

  const archived = useQuery({
    queryKey: queryKeys.crmArchived,
    queryFn: crmApi.archivedCompanies,
    staleTime: 30_000,
  });

  const restore = useMutation({
    mutationFn: (id: number) => crmApi.restoreCompany(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["crm"] });
    },
  });

  const rows = archived.data ?? [];

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs font-medium uppercase tracking-wide text-faint">
        Archived ({rows.length})
      </h3>
      {archived.isLoading ? (
        <p className="text-xs text-faint">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-faint">Nothing is archived.</p>
      ) : (
        <div className="max-h-80 overflow-y-auto rounded-md border border-line">
          <table className="w-full text-xs">
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-line first:border-t-0">
                  <td className="px-2 py-1">
                    <Link to={`/crm/companies/${row.id}`} className="text-brand-600 hover:underline">
                      {row.name}
                    </Link>
                    {row.domain ? <span className="ml-2 text-muted">{row.domain}</span> : null}
                  </td>
                  <td className="px-2 py-1 text-muted">
                    {formatDate(row.archivedAt)}
                    {row.archivedBy ? ` by ${row.archivedBy}` : ""}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={restore.isPending && restore.variables === row.id}
                      onClick={() => restore.mutate(row.id)}
                    >
                      Restore
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {restore.isError ? (
        <p className="text-xs text-sla-breached">
          {restore.error instanceof ApiError ? restore.error.message : "Could not restore it."}
        </p>
      ) : null}
    </section>
  );
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
